// ActivityScopeBar — the Event / Market / User filter shared by all four
// Activity pages (Active Orders, Active Positions, Order History, Trade
// History).
//
// The three filters combine with AND, and every page applies them the
// same way, so they live here once instead of four times. Scope state is
// held in the URL (see useActivityScope) so a filtered view is a link an
// operator can paste into a ticket and reload.
//
// Event filtering is resolved CLIENT-SIDE into the event's member market
// ids, which every upstream list endpoint already accepts as a
// comma-joined ?market_id. That reuses a predicate the services have
// rather than teaching four of them what an event is — none of them
// stores the relationship.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Button } from "@/components/ui/Button";
import { ScopePicker, useDebouncedQuery, type ScopeOption } from "./ScopePicker";
import { fetchRules, fetchTopics, fetchUsers, keys } from "@/lib/queries";

/** One event's member markets is well under this in practice. */
const EVENT_MARKET_CAP = 200;
const SEARCH_LIMIT = 10;

export interface ActivityScope {
  eventId: string;
  eventName: string;
  marketId: string;
  marketName: string;
  userId: string;
  userName: string;
}

export const EMPTY_SCOPE: ActivityScope = {
  eventId: "",
  eventName: "",
  marketId: "",
  marketName: "",
  userId: "",
  userName: "",
};

/** The URL-search shape every Activity route declares. */
export interface ActivityScopeSearch {
  event_id?: string;
  event_name?: string;
  market_id?: string;
  market_name?: string;
  user_id?: string;
  user_name?: string;
}

/**
 * validateActivityScopeSearch — the `validateSearch` every Activity route
 * shares. Ids alone would be enough to filter, but then a reloaded link
 * would show a bare ULID in the filter chip; carrying the display name
 * keeps a pasted URL readable without a lookup round-trip per filter.
 */
export function validateActivityScopeSearch(
  raw: Record<string, unknown>,
): ActivityScopeSearch {
  const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : undefined);
  return {
    event_id: str(raw.event_id),
    event_name: str(raw.event_name),
    market_id: str(raw.market_id),
    market_name: str(raw.market_name),
    user_id: str(raw.user_id),
    user_name: str(raw.user_name),
  };
}

/**
 * useActivityScope — scope state backed by the URL. Returns the current
 * scope plus a setter that rewrites the search params; `resetKey` is a
 * stable string a page can hand to its pagination reset so changing a
 * filter always returns to page 1.
 */
export function useActivityScope(): {
  scope: ActivityScope;
  setScope: (next: ActivityScope) => void;
  resetKey: string;
  hasScope: boolean;
} {
  const search = useSearch({ strict: false }) as ActivityScopeSearch;
  const navigate = useNavigate();

  const scope = useMemo<ActivityScope>(
    () => ({
      eventId: search.event_id ?? "",
      eventName: search.event_name ?? "",
      marketId: search.market_id ?? "",
      marketName: search.market_name ?? "",
      userId: search.user_id ?? "",
      userName: search.user_name ?? "",
    }),
    [search],
  );

  const setScope = (next: ActivityScope) => {
    navigate({
      to: ".",
      // MERGED into whatever else the route keeps in its search, not
      // written over it: a page may hold URL state of its own beside
      // the shared scope (Active Positions holds its currency filter
      // there), and replacing the object wholesale would clear that
      // every time an operator picked a market. Every scope key is
      // named explicitly, so clearing the scope still clears it.
      search: (prev) => ({
        ...prev,
        event_id: next.eventId || undefined,
        event_name: next.eventName || undefined,
        market_id: next.marketId || undefined,
        market_name: next.marketName || undefined,
        user_id: next.userId || undefined,
        user_name: next.userName || undefined,
      }),
      replace: true,
    });
  };

  return {
    scope,
    setScope,
    resetKey: `${scope.eventId}|${scope.marketId}|${scope.userId}`,
    hasScope: Boolean(scope.eventId || scope.marketId || scope.userId),
  };
}

/**
 * useEventMarketScope — the `market_id` query value for a scope.
 *
 * A picked market wins outright. An event alone expands to its member
 * markets, comma-joined. While that expansion is in flight the hook
 * returns `pending: true` and the caller must NOT fire its list query:
 * reading with no market filter would briefly show every market's rows
 * under an event filter, which reads as a broken filter rather than a
 * loading one.
 *
 * An event with no markets yields a sentinel that matches nothing, so an
 * empty event shows an empty page instead of silently unfiltered rows.
 */
export function useEventMarketScope(scope: ActivityScope): {
  marketIDs?: string;
  pending: boolean;
} {
  const needsExpansion = !scope.marketId && Boolean(scope.eventId);
  const members = useQuery({
    queryKey: keys.rulesList(undefined, EVENT_MARKET_CAP, 0, undefined, {
      event_id: scope.eventId,
    }),
    queryFn: () =>
      fetchRules({ limit: EVENT_MARKET_CAP, offset: 0, event_id: scope.eventId }),
    enabled: needsExpansion,
    staleTime: 60_000,
  });

  if (scope.marketId) return { marketIDs: scope.marketId, pending: false };
  if (!needsExpansion) return { marketIDs: undefined, pending: false };
  if (members.isLoading) return { marketIDs: undefined, pending: true };
  const ids = (members.data?.items ?? []).map((m) => m.market_id);
  // "__none__" can't collide with a ULID, so an event whose markets we
  // couldn't resolve filters everything out rather than nothing.
  return { marketIDs: ids.length > 0 ? ids.join(",") : "__none__", pending: false };
}

export function ActivityScopeBar({
  scope,
  onChange,
  children,
}: {
  scope: ActivityScope;
  onChange: (next: ActivityScope) => void;
  /** Page-specific filters (status, side, …) rendered after the scope. */
  children?: React.ReactNode;
}) {
  const [eventQ, setEventQ] = useState("");
  const [marketQ, setMarketQ] = useState("");
  const [userQ, setUserQ] = useState("");
  const eventQuery = useDebouncedQuery(eventQ);
  const marketQuery = useDebouncedQuery(marketQ);
  const userQuery = useDebouncedQuery(userQ);

  const events = useQuery({
    queryKey: keys.topicsList(SEARCH_LIMIT, 0, undefined, undefined, eventQuery),
    queryFn: () =>
      fetchTopics({
        limit: SEARCH_LIMIT,
        offset: 0,
        q: eventQuery,
        // An archived event still has orders and fills against it, so an
        // operator investigating one has to be able to name it.
        include_archived: "true",
      }),
    enabled: !scope.eventId && eventQuery.length >= 2,
  });

  const markets = useQuery({
    queryKey: keys.rulesList(undefined, SEARCH_LIMIT, 0, undefined, {
      event_id: scope.eventId || undefined,
    }, marketQuery),
    queryFn: () =>
      fetchRules({
        limit: SEARCH_LIMIT,
        offset: 0,
        q: marketQuery,
        // Narrow to the picked event when there is one: an operator who
        // has chosen an event is looking for one of ITS markets.
        event_id: scope.eventId || undefined,
      }),
    enabled: !scope.marketId && marketQuery.length >= 2,
  });

  const users = useQuery({
    queryKey: keys.usersList(userQuery, SEARCH_LIMIT, 0),
    queryFn: () => fetchUsers({ limit: SEARCH_LIMIT, offset: 0, q: userQuery }),
    enabled: !scope.userId && userQuery.length >= 2,
  });

  const eventOptions: ScopeOption[] = (events.data?.items ?? []).map((e) => ({
    id: e.event_id,
    label: e.name,
    sublabel: e.event_id,
  }));
  const marketOptions: ScopeOption[] = (markets.data?.items ?? []).map((m) => ({
    id: m.market_id,
    label: m.name,
    sublabel: m.market_id,
  }));
  const userOptions: ScopeOption[] = (users.data?.items ?? []).map((u) => ({
    id: u.user_id,
    label: u.display_name || u.email || u.user_id,
    sublabel: u.email || u.user_id,
  }));

  const selectedEvent = scope.eventId
    ? { id: scope.eventId, label: scope.eventName || scope.eventId }
    : null;
  const selectedMarket = scope.marketId
    ? { id: scope.marketId, label: scope.marketName || scope.marketId }
    : null;
  const selectedUser = scope.userId
    ? { id: scope.userId, label: scope.userName || scope.userId }
    : null;

  return (
    <div className="flex flex-wrap items-end gap-3">
      <ScopePicker
        className="min-w-[200px] flex-1"
        label="Event"
        placeholder="Search events…"
        selected={selectedEvent}
        query={eventQ}
        onQueryChange={setEventQ}
        options={eventOptions}
        loading={events.isLoading}
        onSelect={(o) =>
          onChange({
            ...scope,
            eventId: o?.id ?? "",
            eventName: o?.label ?? "",
            // Clearing or switching the event drops a market pick that
            // belonged to the old one — leaving it would show rows that
            // contradict the event chip beside it.
            marketId: "",
            marketName: "",
          })
        }
      />
      <ScopePicker
        className="min-w-[200px] flex-1"
        label="Market"
        placeholder={scope.eventId ? "Search markets in event…" : "Search markets…"}
        selected={selectedMarket}
        query={marketQ}
        onQueryChange={setMarketQ}
        options={marketOptions}
        loading={markets.isLoading}
        onSelect={(o) =>
          onChange({ ...scope, marketId: o?.id ?? "", marketName: o?.label ?? "" })
        }
      />
      <ScopePicker
        className="min-w-[200px] flex-1"
        label="User"
        placeholder="Search name, email, or ID…"
        selected={selectedUser}
        query={userQ}
        onQueryChange={setUserQ}
        options={userOptions}
        loading={users.isLoading}
        onSelect={(o) =>
          onChange({ ...scope, userId: o?.id ?? "", userName: o?.label ?? "" })
        }
      />
      {children}
    </div>
  );
}

export function ResetScopeButton({
  show,
  onReset,
}: {
  show: boolean;
  onReset: () => void;
}) {
  if (!show) return null;
  return (
    <Button variant="ghost" onClick={onReset}>
      Reset
    </Button>
  );
}
